import React, { useState, useEffect } from "react";
import RoundedButton from "../../../Buttons/RoundedButton";
import TagSelectInput from "../../../UI/Inputs/TagSelectInput";
import { useTranslation } from "react-i18next";
import TagInputSignUp from "../../../UI/Inputs/TagInputSignUp";
import useFetch from "../../../../hooks/useFetch";
import {
  CUSTOM_BIRTH_DATE,
  CUSTOM_INVITE_CODE,
  CUSTOM_OCCUPATION,
} from "../../../../constants/users_login";
import {
  checkInformationValidations,
  checkValidationCode,
} from "../../../../helpers";
import * as kApi from "../../../../constants/api";
import { Box } from "@mui/material";
import TermsAndPrivacyLinks from "./TermsAndPrivacyLinks";
import RoundedButtonOutlined from "components/Buttons/RoundedButtonOutlined";

const Information = (props) => {
  const { t } = useTranslation();
  const [validCode, setValidCode] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showTextError, setShowTextError] = useState(false);
  const [parent, parent_isFetching, parent_error, fetchParent] = useFetch({
    autoInit: false,
    noAuth: true,
  });
  const { hasNick, hasPhone, hasBirthday, hasOccupation } =
    checkInformationValidations(
      props.signUpForm.given_name,
      props.signUpForm.phone,
      props.signUpForm[CUSTOM_BIRTH_DATE],
      props.signUpForm[CUSTOM_OCCUPATION],
    );

  const { hasCode } = checkValidationCode(
    String(props.signUpForm[CUSTOM_INVITE_CODE]),
    validCode,
  );

  // Valida a data de nascimento e retorna o tipo de erro.
  function getBirthDateError() {
    // Valor da data vindo do formulário
    const value = props?.signUpForm[CUSTOM_BIRTH_DATE];

    if (!value) return false;

    // Converte a string do input (YYYY-MM-DD) para objeto Date
    const date = new Date(value);
    const today = new Date();

    // verifica se data é maior que hoje
    if (date > today) {
      return "future";
    }

    // Calcula a data limite de idade (150 anos atrás)
    const maxAge = new Date();
    maxAge.setFullYear(maxAge.getFullYear() - 150);

    if (date < maxAge) {
      return "tooOld";
    }

    return false;
  }

  // Mapeia os tipos de erro para as mensagens traduzidas
  const birthDateMessages = {
    future: t("global.birthdate_cannot_be_future"),
    tooOld: t("global.invalid_birth_date"),
  };

  // Executa a validação da data
  const birthDateError = getBirthDateError();

  // Seleciona a mensagem correta de erro.=, ou a mensagem padrão
  const birthDateErrorMessage =
    birthDateMessages[birthDateError] || t("global.enter_a_date_of_birth");

  useEffect(() => {
    if (props.signUpForm[CUSTOM_INVITE_CODE].length === 8) {
      fetchParent({
        url: kApi.API_Q_CODE + "/" + props.signUpForm[CUSTOM_INVITE_CODE],
        onSuccess: () => {
          setValidCode(true);
        },
        onError: () => {},
      });
    }
    if (parent_error.data?.message.length > 0) {
      setValidCode(false);
    }
  }, [props.signUpForm[CUSTOM_INVITE_CODE]]);

  useEffect(() => {
    if (props.signUpForm[CUSTOM_INVITE_CODE].length === 8) {
      fetchParent({
        url: kApi.API_Q_CODE + "/" + props.signUpForm[CUSTOM_INVITE_CODE],
        onSuccess: () => {
          setValidCode(true);
        },
        onError: (resp) => {
          console.log(resp.message);
        },
      });
    }
    if (parent_error.data?.message.length) {
      setValidCode(false);
    }
  }, []);

  useEffect(() => {
    if (
      Object.values(
        checkInformationValidations(
          props.signUpForm.given_name,
          props.signUpForm.phone,
          props.signUpForm[CUSTOM_BIRTH_DATE],
          props.signUpForm[CUSTOM_OCCUPATION],
        ),
      ).every((item) => item === true)
    ) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [props]);

  useEffect(() => {
    if (props.signUpForm[CUSTOM_OCCUPATION].length > 0) setShowTextError(true);
  }, [props.signUpForm[CUSTOM_OCCUPATION]]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          width: "100%",
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 0, md: 10 },
            width: "100%",
          }}
        >
          <TagInputSignUp
            type="text"
            correctionSafari={true}
            name="given_name"
            label={t("global.nickname_or_name")}
            placeholder={t("global.nickname_or_name")}
            value={props.signUpForm.given_name}
            onChange={props.onChange}
            errorButton={!hasNick}
            showTextError={showTextError}
            textError={t("global.enter_a_nickname_or_name")}
            required
            sx={{ flex: 1, minWidth: 0 }}
          />

          <TagInputSignUp
            type="text"
            name="phone"
            label={t("global.whatsapp")}
            placeholder={"(00) 00000-0000"}
            value={props.signUpForm.phone}
            onChange={props.onChange}
            errorButton={!hasPhone}
            showTextError={showTextError}
            textError={t("global.enter_a_phone")}
            required
            sx={{ flex: 1, minWidth: 0 }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 0, md: 10 },
            width: "100%",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TagInputSignUp
              type="date"
              name={CUSTOM_BIRTH_DATE}
              label={t("global.birthdate")}
              placeholder={t("global.birthdate")}
              value={props.signUpForm[CUSTOM_BIRTH_DATE]}
              max="9999-12-31"
              onChange={props.onChange}
              errorButton={!hasBirthday || !!birthDateError}
              showTextError={showTextError || !!birthDateError}
              textError={birthDateErrorMessage}
              required
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TagSelectInput
              name={CUSTOM_OCCUPATION}
              label={t("global.ocupation")}
              onChange={props.onChange}
              setSignUpForm={props.setSignUpForm}
              signUpForm={props.signUpForm}
              value={props.signUpForm[CUSTOM_OCCUPATION]}
              errorButton={!hasOccupation}
              showTextError={showTextError}
              textError={t("global.Enter_your_occupation")}
            />
          </Box>
        </Box>

        {props.signUpForm.profile === "patient" && (
          <Box sx={{ marginTop: "25px" }}>
            <TagInputSignUp
              type="text"
              name={CUSTOM_INVITE_CODE}
              label={t("global.do_you_have_an_invitation_code")}
              placeholder={t("global.field_not_required")}
              value={String(props.signUpForm[CUSTOM_INVITE_CODE])}
              onChange={props.onChange}
              maxLength={8}
              errorButton={!hasCode}
              showTextError={!hasCode}
              textError={t("global.Enter_a_valid_code")}
              required={false}
            />
          </Box>
        )}
      </Box>

      {/* Seção de termos e políticas */}
      <TermsAndPrivacyLinks />

      {/* Botões de navegação */}
      <Box
        sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mt: 1 }}
      >
        {/* Botão voltar */}
        <RoundedButtonOutlined
          text={t("global.back")}
          onClick={() => {
            props.setReload(true);
            props.setStep("LoginData");
          }}
          sx={{
            width: " 200px",
          }}
        />

        {/* Botão próximo */}
        <RoundedButton
          disable={
            !hasNick || !hasBirthday || !hasOccupation || !!birthDateError
          }
          text={t("global.next")}
          onClick={() => {
            props.setReload(true);
            setShowTextError(true);
            showError && hasCode && props.createUser();
          }}
          sx={{
            width: " 200px",
          }}
        />
      </Box>
    </Box>
  );
};
export default Information;
